import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { SessionStorage } from 'common/CookieStorage'
import { STORAGEKEYS } from 'enviroment/Keys'
import { AppState } from 'store'
import { setCurrentClientPeselReservedErrorText, setCurrentFullSmsCode } from 'store/ContractSignSlice/ContractSignSlice'
import { HttpClient } from 'common/HttpClient'
import { Environment } from 'enviroment/AppSettings'
import { ConnectionState } from 'store/ApplicationSlice/middleware/credential-actions-models'
import applicationService from 'service/ApplicaionService/ApplicationService'
import { AppLinks } from 'enviroment/AppLinks'
import { SMS_CODE_STATUSES_FROM_ALPHA } from 'service/ApplicaionService/ApplicationServiceModels'
import { sleep } from 'common/helpers/helpers'
import { UseClientPeselReservedPageLogicReturnProps } from '../ClientPeselReservedPageProps'
import useTextContents from 'common/hooks/useTextContents'


const useClientPeselReservedPageLogic = (): UseClientPeselReservedPageLogicReturnProps => {

   const sessionStorage = new SessionStorage()
   const clientPeselReservedTextContents = useTextContents()
   const dispatch = useDispatch()
   const navigate = useNavigate()

   const contractSignState = useSelector((state: AppState) => state.contractSignState)
   const globalConfig = useSelector((state: AppState) => state.globalConfigurationState.globalConfig)

   const { currentFullSmsCode, currentClientPeselReservedErrorText } = contractSignState
   const { clientPeselReservedCheckBlockingTimeInSec: DEFAULT_CLIENT_PESEL_RESERVED_CHECK_BLOCKING_TIME_IN_SEC } = globalConfig
   
   
//>>>>>>>>> STATE'S
   /**
    * Czy przycisk jest w stanie "ładowania" 
   */
   const [isButtonLoading, setIsButtonLoading] = useState(false)

   /**
    * Gdy odliczanie jest aktywne (CLIENT_PESEL_RESERVED_IS_INTERVAL_RUNNING jest ustawione na 'true'), 
    * lub gdy użytkownik osiągnął już limit ilości wywołań funkcji pod przyciskiem "Sprawdź" - przycisk "Sprawdź" jest blokowany.
   */
   const [isButtonBlocked, setIsButtonBlocked] = useState<boolean>(() => {
      const isIntervalCurrentlyRunning = sessionStorage.get(STORAGEKEYS.CLIENT_PESEL_RESERVED_IS_INTERVAL_RUNNING)
   
      return isIntervalCurrentlyRunning === 'true'? true : false
   })

   /**
      * W momencie odświeżenia strony, sprawdzamy w zmiennej z sessionStorage, przez jaki czas interwał ma być jeszcze aktywny.
      * Jeśli takiej zmiennej nie ma jeszcze ustawionej, ustawiamy domyślnie na 0. Dopiero przy kliknięciu przycisku "Sprawdź" 
      * i rozpoczęciu interwału ustawiamy "pełną" wartość.
   */
   const [buttonCheckBlockTimeInSeconds, setButtonCheckBlockTimeInSeconds] = useState(() => {
      const savedSeconds = sessionStorage.get(STORAGEKEYS.CLIENT_PESEL_RESERVED_CURRENT_BLOCKING_TIME_IN_SECONDS)

      if(savedSeconds === undefined || savedSeconds === null) {
         return 0
      }

      return parseInt(savedSeconds, 10)
   })
//>>>>>>>>>

   /**
    * Funkcja wywoływana przy kliknięciu przycisku "Sprawdź", lub po odświeżeniu strony, ale tylko w momencie gdy obecnie uruchomiony interwał, 
    * jeszcze nie dobiegł końca (Zmienna CLIENT_PESEL_RESERVED_IS_INTERVAL_RUNNING w sessionStorage, ustawiona na 'true')
    * 
    * Gdy funkcja uruchamiana jest przez użytkownika (kliknięcie przycisku "Sprawdź"), przekazujemy parametr @param isFunctionInvokedByUser = true,
    * co pozwala nam na wykonanie dodatkowej logiki, charakterystycznej tylko dla uruchomienia funkcji przez użytkownika.
    * 
    * Gdy funkcja zostanie uruchomiona poprzez useEffect (po odświeżeniu strony), niczego nie ustawiamy, tylko kontynuujemy odliczanie za pomocą setInterval.
   */
   const startOrContinueCodeInterval = async(isFunctionInvokedByUser?: boolean): Promise<void> => {
      if(isFunctionInvokedByUser) { // Te wartości są zmieniane, jedynie gdy funkcja zostanie wywołana przez użytkownika (kliknięcie w przycisk na stronie)
         sessionStorage.changeItemValue(STORAGEKEYS.CLIENT_PESEL_RESERVED_IS_INTERVAL_RUNNING, 'true')

         setIsButtonBlocked(true)
         setButtonCheckBlockTimeInSeconds(DEFAULT_CLIENT_PESEL_RESERVED_CHECK_BLOCKING_TIME_IN_SEC)
      }
         
      const intervalId = setInterval(() => {
         setButtonCheckBlockTimeInSeconds((prevSeconds) => {
            if (prevSeconds > 0) {
               sessionStorage.changeItemValue(STORAGEKEYS.CLIENT_PESEL_RESERVED_CURRENT_BLOCKING_TIME_IN_SECONDS, (prevSeconds - 1).toString())
               return prevSeconds - 1
            } 
            else {
               // CLEANUP (zakończenie odliczania)
               clearInterval(intervalId)
               sessionStorage.add(STORAGEKEYS.CLIENT_PESEL_RESERVED_CURRENT_BLOCKING_TIME_IN_SECONDS, "0")
               sessionStorage.changeItemValue(STORAGEKEYS.CLIENT_PESEL_RESERVED_IS_INTERVAL_RUNNING, 'false')

               setIsButtonBlocked(false)
               return 0
            }
         })
      }, 1000)

      return
   }

   /**
    * Po kliknięciu przez użytkownika, próbujemy podpisać umowę, zapisanym na wcześniejszym ekranie kodem sms.
    * Jeśli dostaniemy pozytywną odpowiedź z Backendu - użytkownik odstrzegł PESEL - kierujemy go na ekran decyzji pozytywnej.
    * Jeśli użytkownik nie odstrzegł numeru PESEL, a kliknął w przycisk, dostaniemy stosowny błąd z Backendu. 
    * Jeśli użytkownik wykorzystał limit prób (o czym poinformuje specjalny kod z Backendu), kierujemy go na ekran "czekadełka". 
    * Z tego ekranu gamma skieuje go na ekran decyzji negatywnej, uprzednio odrzucając jego wniosek.
    * @returns { void }
    */
   const handleOnButtonCheckClick = async () => {
      try {
         const client = new HttpClient(Environment.apiUrl)
         const result = (await client.get<ConnectionState>("connection", true)).data
         
         if (!result.isFinished && currentFullSmsCode) {
            setIsButtonLoading(true)
            await sleep(1500) // W przypadku gdy zapytanie wykona się zbyt szybko, zatrzymujemy użytkownika na 1,5 sekundy - dla efektu wizualnego animacji ładowania przycisku.
            const signApplicationResponse = (await applicationService.signApplication({ smsCode: currentFullSmsCode }).then((p) => p))


            if(signApplicationResponse.isOk){ // Jeśli udało się podpisać, NIE STARTUJEMY interwału, czyścimy kod sms i kierujemy na ekran sukcesu
               navigate(AppLinks.success)
               dispatch(setCurrentFullSmsCode("")) // Czyścimy zapisany w state kod sms
               return
            }
            else if(signApplicationResponse.code === SMS_CODE_STATUSES_FROM_ALPHA.CLIENT_PESEL_RESERVED){ // Jeśli PESEL dalej zastrzeżony, startujemy interwał i wyświetlamy odpowiedni tekst
              dispatch(setCurrentClientPeselReservedErrorText(clientPeselReservedTextContents('clientPeselReservedPage.errorMessages.clientPeselStillReserved')))
              setIsButtonLoading(false)
              await startOrContinueCodeInterval(true)
            }
            else if(signApplicationResponse.code === SMS_CODE_STATUSES_FROM_ALPHA.CLIENT_PESEL_RESERVED_LIMIT_REACHED){ //Jeśli osiągnięto limit, czyścimy kod sms, NIE STARTUJEMY interwału i kierujemy na ekran "czekadełka".
               navigate(AppLinks.waiting)
               dispatch(setCurrentFullSmsCode("")) // Czyścimy zapisany w state kod sms
               return
            }
         }
      }
      catch (e) { // Błędna odpowiedź 
         dispatch(setCurrentClientPeselReservedErrorText(clientPeselReservedTextContents('clientPeselReservedPage.errorMessages.somethingWentWrong')))

         setIsButtonLoading(false)
         await startOrContinueCodeInterval(true)
      }
   }

//>>>>>>>>> USE EFFECT
   /**
    * Po każdym odświeżeniu strony, sprawdzamy czy aktualnie nie jest uruchomiony interwał z funkcji podpiętej pod przycisk "Sprawdź".
    * Jeśli jest uruchomiony, znaczy że odświeżanie go przerwało - wywołujemy go ponownie i kontynuujemy.
   */
   useEffect(() => {
      const isIntervalCurrentlyRunning = sessionStorage.get(STORAGEKEYS.CLIENT_PESEL_RESERVED_IS_INTERVAL_RUNNING)

      if(isIntervalCurrentlyRunning === 'true'){
         startOrContinueCodeInterval()
      }
   }, [])

//>>>>>>>>> 

   return {
      currentClientPeselReservedErrorText,
      isButtonBlocked,
      isButtonLoading,

      handleOnButtonCheckClick,
      clientPeselReservedTextContents,
   }
}


export default useClientPeselReservedPageLogic